import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Grid3x3, List, Eraser, Plus, HelpCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileItem } from './FileItem';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface ProjectFile {
  id: string;
  name: string;
  displayName?: string; // Name with green screen indicator
  fileType?: 'video' | 'audio' | 'image' | 'srt';
  thumbnailUrl?: string;
  duration: number;
  timestamp?: string;
  projectName?: string;
  sessionName?: string;
  groupName?: string;
  takeName?: string;
  talent?: string[];
  s3Url?: string;
  s3Key?: string;
  imported?: boolean;
  liked?: boolean;
  comments?: any[];
  enhanced?: boolean;
  enhancementPrompt?: string;
  analysisData?: {
    transcript?: any;
    background?: string;
  };
  layerType?: 'main' | 'background' | 'audio';
  linkedAudioId?: string;
  linkedVideoId?: string;
  linkedPairId?: string;
  hasGreenScreen?: boolean;
  greenScreenUrl?: string;
  greenScreenKey?: string;
  greenScreenType?: 'green-screen' | 'foreground-mask' | 'alpha-mask';
  hasAudio?: boolean;
  // AI Generation fields
  isAIGenerated?: boolean;
  aiGenerationPrompt?: string;
  aiAssetType?: 'image' | 'video' | 'music';
}

interface ProjectBinProps {
  files: ProjectFile[];
  onFileSelect: (fileId: string) => void;
  selectedFiles: Set<string>;
  onDragStart: (file: ProjectFile) => void;
  onAddToTimeline?: (file: ProjectFile) => void;
  onAddToTimelineWithOptions?: (file: ProjectFile, options: { includeMask?: boolean; videoOnly?: boolean }) => void;
  onRemoveBackground?: (fileIds: string[], outputType?: string) => void;
  onAnalyze?: (files: ProjectFile[]) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  analysisResults?: {[key: string]: any};
  onInsertText?: (text: string, file?: ProjectFile, startTime?: number, endTime?: number) => void;
  onFileRemove?: (fileId: string) => void;
  onTranscriptEdit?: (fileId: string, segmentIndex: number, newText: string) => void;
  onSpeakerEdit?: (fileId: string, segmentIndex: number, newSpeaker: string) => void;
  onOpenAIGenerator?: () => void;
  compact?: boolean; // Compact mode for sidebar display
}

export const ProjectBin: React.FC<ProjectBinProps> = ({
  files,
  onFileSelect,
  selectedFiles,
  onDragStart,
  onAddToTimeline,
  onAddToTimelineWithOptions,
  onRemoveBackground,
  onAnalyze,
  onSelectAll,
  onDeselectAll,
  analysisResults,
  onInsertText,
  onFileRemove,
  onTranscriptEdit,
  onSpeakerEdit,
  onOpenAIGenerator,
  compact = false,
}) => {
  // Debug: Check if files are being passed to ProjectBin
  React.useEffect(() => {
    console.log('📁 ProjectBin received files:', files.length, files.map(f => f.name));
  }, [files]);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [heyJoeCollapsed, setHeyJoeCollapsed] = useState(false);
  const [uploadsCollapsed, setUploadsCollapsed] = useState(false);
  const [aiAssetsCollapsed, setAiAssetsCollapsed] = useState(false);

  // Group files by import source and AI generation
  // Filter out mask files - they're shown as part of their linked original
  const visibleFiles = files.filter(f => f.layerType !== 'mask');
  const aiGeneratedFiles = visibleFiles.filter(f => f.isAIGenerated);
  const heyJoeFiles = visibleFiles.filter(f => f.imported && !f.isAIGenerated);
  const uploadedFiles = visibleFiles.filter(f => !f.imported && !f.isAIGenerated);
  
  // Debug logging
  console.log('ProjectBin - Total files:', files.length, '(visible:', visibleFiles.length, ')');
  console.log('ProjectBin - Hey Joe files:', heyJoeFiles.length);
  console.log('ProjectBin - Uploaded files:', uploadedFiles.length);
  console.log('ProjectBin - All files:', files.map(f => ({ name: f.name, imported: f.imported, layerType: f.layerType })));

  const allSelected = visibleFiles.length > 0 && selectedFiles.size === visibleFiles.length;
  const someSelected = selectedFiles.size > 0 && selectedFiles.size < visibleFiles.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onDeselectAll?.();
    } else {
      onSelectAll?.();
    }
  };

  // Dynamic grid class based on compact mode
  const gridClass = compact
    ? (viewMode === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-2')
    : (viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-4 gap-2' : 'space-y-2');

  return (
    <Card className={compact ? "border-0 shadow-none bg-transparent p-0" : "border-border"}>
      <CardHeader className={compact ? "pb-2 px-0 pt-0" : "pb-3"}>
        <div className="flex items-center justify-between">
          <CardTitle className={compact ? "text-sm font-medium flex items-center gap-2" : "text-lg font-semibold flex items-center gap-2"}>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            Project Bin
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p>Your imported and uploaded files. Drag files to the timeline, or select multiple and click "Add to Timeline".</p>
              </TooltipContent>
            </Tooltip>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {visibleFiles.length} file{visibleFiles.length !== 1 ? 's' : ''}
              {selectedFiles.size > 0 && ` (${selectedFiles.size} selected)`}
            </span>

            {!isCollapsed && (
              <>
                {/* View Mode Toggle */}
                <div className="flex border rounded-md flex-shrink-0">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 rounded-r-none"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3x3 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2 rounded-l-none"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-3.5 w-3.5" />
                  </Button>
                </div>

              </>
            )}
          </div>
        </div>

        {/* Action Buttons for selected files - shown below header in compact mode */}
        {!isCollapsed && selectedFiles.size > 0 && (() => {
          // Count only video files in selection
          const selectedVideoCount = Array.from(selectedFiles).filter(id => {
            const file = files.find(f => f.id === id);
            return file?.fileType === 'video';
          }).length;

          return (
            <div className={compact ? "flex flex-wrap gap-2 mt-2" : "flex items-center gap-2 mt-2"}>
              {/* Add to Timeline - works for any file type */}
              {onAddToTimeline && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() => {
                    // Add selected files to timeline in order
                    Array.from(selectedFiles).forEach(id => {
                      const file = files.find(f => f.id === id);
                      if (file) onAddToTimeline(file);
                    });
                  }}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {compact ? `Add (${selectedFiles.size})` : `Add to Timeline (${selectedFiles.size})`}
                </Button>
              )}

              {/* Remove BG - only for video files */}
              {onRemoveBackground && selectedVideoCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() => onRemoveBackground(Array.from(selectedFiles))}
                >
                  <Eraser className="h-3.5 w-3.5 mr-1" />
                  {compact ? `BG (${selectedVideoCount})` : `Remove BG (${selectedVideoCount})`}
                </Button>
              )}

              {/* Analyze button removed - clips are auto-analyzed on import */}
            </div>
          );
        })()}

        {/* Select All Checkbox */}
        {!isCollapsed && visibleFiles.length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
            <Checkbox
              checked={allSelected}
              onCheckedChange={handleSelectAll}
              className={someSelected ? 'data-[state=checked]:bg-primary/60' : ''}
            />
            <span className="text-sm text-muted-foreground">
              Select All
            </span>
          </div>
        )}
      </CardHeader>

      {!isCollapsed && (
        <CardContent className={compact ? "space-y-3 px-0 pb-0" : "space-y-4"}>
          {/* Hey Joe Import Section */}
          {heyJoeFiles.length > 0 && (
            <div className="space-y-2">
              <button 
                className="text-sm font-medium text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors w-full text-left"
                onClick={() => setHeyJoeCollapsed(!heyJoeCollapsed)}
              >
                {heyJoeCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                Hey Joe Import ({heyJoeFiles.length})
              </button>
              {!heyJoeCollapsed && (
                <div className={gridClass}>
                {heyJoeFiles.map((file) => {
                  // Find analysis results for this file - prefer most recent with transcript data
                  const fileAnalysis = analysisResults && (() => {
                    const allMatches = Object.values(analysisResults).filter((result: any) => {
                      if (result.status === 'completed') {
                        // Check multiple possible matches
                        return result.video_id === file.id || 
                               result.video_id === file.name ||
                               (result.results && result.results.video_id === file.id);
                      }
                      return false;
                    });
                    
                    if (allMatches.length === 0) return null;
                    
                    // Sort by preference: with transcript data first, then by timestamp (most recent)
                    allMatches.sort((a: any, b: any) => {
                      // Prefer results with transcript data
                      const aHasTranscript = !!(a.transcript || a.results?.transcript);
                      const bHasTranscript = !!(b.transcript || b.results?.transcript);
                      
                      if (aHasTranscript && !bHasTranscript) return -1;
                      if (!aHasTranscript && bHasTranscript) return 1;
                      
                      // Both have same transcript status, sort by timestamp (most recent first)
                      const aTime = new Date(a.timestamp || a.results?.timestamp || 0).getTime();
                      const bTime = new Date(b.timestamp || b.results?.timestamp || 0).getTime();
                      return bTime - aTime;
                    });
                    
                    return allMatches[0];
                  })();
                  
                  // Debug logging
                  if (file.fileType === 'video' && analysisResults && Object.keys(analysisResults).length > 0) {
                    console.log(`🔍 Matching analysis for file ${file.id}:`, fileAnalysis);
                  }
                  
                  const fileWithAnalysis = fileAnalysis ? {
                    ...file,
                    analysisData: {
                      transcript: fileAnalysis.transcript || fileAnalysis.results?.transcript,
                      background: fileAnalysis.background || fileAnalysis.results?.background,
                    }
                  } : file;
                  
                  // Debug: Check if analysis status is preserved
                  if (file.analysisStatus) {
                    console.log('🔍 ProjectBin - File has analysisStatus:', file.id, file.analysisStatus);
                    console.log('🔍 ProjectBin - fileWithAnalysis has analysisStatus:', fileWithAnalysis.analysisStatus);
                  }
                  
                  // TEST: Debug FileItem rendering
                  console.log('🎬 ProjectBin about to render FileItem for:', file.name);
                  
                  return (
                    <FileItem
                      key={file.id}
                      file={fileWithAnalysis}
                      isSelected={selectedFiles.has(file.id)}
                      onSelect={() => onFileSelect(file.id)}
                      onDragStart={() => onDragStart(file)}
                      onAddToTimeline={onAddToTimeline ? () => onAddToTimeline(file) : undefined}
                      onAddToTimelineWithOptions={onAddToTimelineWithOptions ? (options) => onAddToTimelineWithOptions(file, options) : undefined}
                      viewMode={viewMode}
                      onInsertText={onInsertText}
                      onFileRemove={onFileRemove}
                      onTranscriptEdit={onTranscriptEdit}
                      onSpeakerEdit={onSpeakerEdit}
                    />
                  );
                })}
                </div>
              )}
            </div>
          )}

          {/* AI Assets Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <button
                className="text-sm font-medium text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors text-left"
                onClick={() => setAiAssetsCollapsed(!aiAssetsCollapsed)}
              >
                {aiAssetsCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                <Sparkles className="h-3 w-3 text-purple-500" />
                AI Assets ({aiGeneratedFiles.length})
              </button>
              {onOpenAIGenerator && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-xs gap-1"
                  onClick={onOpenAIGenerator}
                >
                  <Sparkles className="h-3 w-3" />
                  Generate
                </Button>
              )}
            </div>
            {!aiAssetsCollapsed && (
              <>
                {aiGeneratedFiles.length > 0 ? (
                  <div className={gridClass}>
                    {aiGeneratedFiles.map((file) => (
                      <FileItem
                        key={file.id}
                        file={file}
                        isSelected={selectedFiles.has(file.id)}
                        onSelect={() => onFileSelect(file.id)}
                        onDragStart={() => onDragStart(file)}
                        onAddToTimeline={onAddToTimeline ? () => onAddToTimeline(file) : undefined}
                        onAddToTimelineWithOptions={onAddToTimelineWithOptions ? (options) => onAddToTimelineWithOptions(file, options) : undefined}
                        viewMode={viewMode}
                        onInsertText={onInsertText}
                        onFileRemove={onFileRemove}
                        onTranscriptEdit={onTranscriptEdit}
                        onSpeakerEdit={onSpeakerEdit}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground border border-dashed rounded-lg">
                    <Sparkles className="h-6 w-6 mx-auto mb-2 text-purple-500/50" />
                    <p className="text-xs">No AI assets yet</p>
                    {onOpenAIGenerator && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-2 h-6 text-xs text-purple-500 hover:text-purple-600 hover:bg-transparent"
                        onClick={onOpenAIGenerator}
                      >
                        Generate your first AI asset
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Uploaded Files Section */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              <button
                className="text-sm font-medium text-muted-foreground flex items-center gap-2 hover:text-foreground transition-colors w-full text-left"
                onClick={() => setUploadsCollapsed(!uploadsCollapsed)}
              >
                {uploadsCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                Uploads ({uploadedFiles.length})
              </button>
              {!uploadsCollapsed && (
                <div className={gridClass}>
                {uploadedFiles.map((file) => {
                  // Find analysis results for this file - prefer most recent with transcript data
                  const fileAnalysis = analysisResults && (() => {
                    const allMatches = Object.values(analysisResults).filter((result: any) => {
                      if (result.status === 'completed') {
                        // Check multiple possible matches
                        return result.video_id === file.id || 
                               result.video_id === file.name ||
                               (result.results && result.results.video_id === file.id);
                      }
                      return false;
                    });
                    
                    if (allMatches.length === 0) return null;
                    
                    // Sort by preference: with transcript data first, then by timestamp (most recent)
                    allMatches.sort((a: any, b: any) => {
                      // Prefer results with transcript data
                      const aHasTranscript = !!(a.transcript || a.results?.transcript);
                      const bHasTranscript = !!(b.transcript || b.results?.transcript);
                      
                      if (aHasTranscript && !bHasTranscript) return -1;
                      if (!aHasTranscript && bHasTranscript) return 1;
                      
                      // Both have same transcript status, sort by timestamp (most recent first)
                      const aTime = new Date(a.timestamp || a.results?.timestamp || 0).getTime();
                      const bTime = new Date(b.timestamp || b.results?.timestamp || 0).getTime();
                      return bTime - aTime;
                    });
                    
                    return allMatches[0];
                  })();
                  
                  // Debug logging
                  if (file.fileType === 'video' && analysisResults && Object.keys(analysisResults).length > 0) {
                    console.log(`🔍 Matching analysis for file ${file.id}:`, fileAnalysis);
                  }
                  
                  const fileWithAnalysis = fileAnalysis ? {
                    ...file,
                    analysisData: {
                      transcript: fileAnalysis.transcript || fileAnalysis.results?.transcript,
                      background: fileAnalysis.background || fileAnalysis.results?.background,
                    }
                  } : file;
                  
                  // Debug: Check if analysis status is preserved
                  if (file.analysisStatus) {
                    console.log('🔍 ProjectBin - File has analysisStatus:', file.id, file.analysisStatus);
                    console.log('🔍 ProjectBin - fileWithAnalysis has analysisStatus:', fileWithAnalysis.analysisStatus);
                  }
                  
                  // TEST: Debug FileItem rendering
                  console.log('🎬 ProjectBin about to render FileItem for:', file.name);
                  
                  return (
                    <FileItem
                      key={file.id}
                      file={fileWithAnalysis}
                      isSelected={selectedFiles.has(file.id)}
                      onSelect={() => onFileSelect(file.id)}
                      onDragStart={() => onDragStart(file)}
                      onAddToTimeline={onAddToTimeline ? () => onAddToTimeline(file) : undefined}
                      onAddToTimelineWithOptions={onAddToTimelineWithOptions ? (options) => onAddToTimelineWithOptions(file, options) : undefined}
                      viewMode={viewMode}
                      onInsertText={onInsertText}
                      onFileRemove={onFileRemove}
                      onTranscriptEdit={onTranscriptEdit}
                      onSpeakerEdit={onSpeakerEdit}
                    />
                  );
                })}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {visibleFiles.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm mb-3">Supports: Videos, Audio, Images, SRT Subtitles</p>
              <p className="text-sm mb-2">💡 Select multiple files - order matters for AI enhancement!</p>
              <p className="text-xs">Or use Import button above to get videos from Casting</p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
